/**
 * Finds the paragraphs that make up a document's table of contents and, for
 * each entry, the bookmark of the heading it lists.
 *
 * A table of contents is one complex field: a `w:fldChar begin` and an
 * instruction that starts with `TOC` in its first paragraph, the entries as the
 * paragraphs that follow, and the `w:fldChar end` in its last paragraph. Word
 * rewrites every paragraph between the two markers whenever the field updates,
 * so a change written into one is thrown away. Each entry names the heading it
 * lists: with the `\h` switch as a `w:hyperlink` whose `w:anchor` is the
 * heading's `_Toc…` bookmark, and always through the `PAGEREF _Toc…` field
 * that draws its page number. This module reads that name so an edit aimed at
 * an entry can be moved to the heading it lists.
 *
 * A contents list can also be typed: a run of paragraphs that are each one
 * internal link to a heading, the shape the agent builds when it types the
 * entries and links each to its heading. Nothing rewrites those entries, so an
 * edit on the heading they list is copied onto them. They are reported as
 * entries `listedBy` a `linked_list`; the field's entries are `listedBy` a
 * `field`.
 *
 * WHY the field markers and not the content control
 *   Word wraps a table of contents it inserted itself in a `w:sdt` with the
 *   "Table of Contents" gallery, but a table of contents pasted in, or built by
 *   another program, carries only the field. The field is the one thing every
 *   table of contents has.
 */
import { elementNameWithoutPrefix, FIELD_CHAR_TAG, FIELD_CHAR_TYPES, TEXT_TAGS, WORDPROCESSINGML_NS } from "./ooxml-names";

/** How an entry is kept in step with its heading: Word rewrites a `field` entry on update; nothing rewrites a `linked_list` entry. */
export type TableOfContentsListing = "field" | "linked_list";

/** Which heading a table of contents entry lists, by the bookmark name the entry points at (null for an entry that points nowhere), and how the entry is listed. */
export interface TableOfContentsEntryTarget {
    anchorBookmarkName: string | null;
    listedBy: TableOfContentsListing;
}

/** A complex field still open while the paragraphs are walked: the instruction text gathered so far and the paragraph its `begin` sits in. */
interface OpenField {
    instruction: string;
    beganAtParagraphIndex: number;
}

/** The paragraphs a `TOC` field spans, both ends inclusive, as indexes into the walked paragraphs. */
interface TableOfContentsSpan {
    firstParagraphIndex: number;
    lastParagraphIndex: number;
}

/** A complex field open inside one paragraph while its links are counted: its instruction, and whether the walk is past its `separate`, in the result Word shows. */
interface OpenFieldInParagraph {
    instruction: string;
    inResult: boolean;
}

/** What one paragraph's links add up to: the characters inside internal links, the non-blank ones outside, the first link's target, and every target its links name. */
interface ParagraphLinkTally {
    linkedCharacterCount: number;
    unlinkedCharacterCount: number;
    firstAnchorBookmarkName: string | null;
    /**
     * Every distinct place the paragraph's internal links point to.
     *
     * EXAMPLE
     *   link("_h1") "1. Change kinds" + link("_h1") "3"   -> { "_h1" }: a title and its page number, one place
     *   link("_h1") "1. Change kinds" + link("_h3") "Fees" -> { "_h1", "_h3" }: two places
     */
    distinctAnchorBookmarkNames: Set<string>;
}

/** The field instruction of a table of contents begins with this word: ` TOC \o "1-3" \h \z \u `. */
const TABLE_OF_CONTENTS_INSTRUCTION = /^\s*TOC\b/i;
/** The instruction that draws an entry's page number names the heading's bookmark: ` PAGEREF _Toc123 \h `. */
const PAGE_REFERENCE_INSTRUCTION = /^\s*PAGEREF\s+(\S+)/i;
/** The instruction of a link to a place in this document names its bookmark: ` HYPERLINK \l "_Toc123" `. */
const INTERNAL_LINK_INSTRUCTION = /^\s*HYPERLINK\s+\\l\s+"?([^"\s]+)"?/i;
/**
 * How many consecutive linked paragraphs make a contents list.
 *
 * WHY three
 *   Two paragraphs that are each one cross-reference stand next to each other
 *   in ordinary prose; a list of three or more is a table of contents.
 */
const LINKED_LIST_MINIMUM_ENTRY_COUNT = 3;

const FIELD_INSTRUCTION_TAG = "instrText";
const FIELD_SIMPLE_TAG = "fldSimple";
const HYPERLINK_TAG = "hyperlink";
/** `Node.ELEMENT_NODE`, spelled out because the DOM the server runs on may not expose the constant. */
const ELEMENT_NODE_TYPE = 1;

export class TableOfContents {
    /**
     * Every paragraph inside a table of contents, mapped to the heading bookmark
     * its entry points at, and every paragraph of a linked contents list.
     *
     * CASES
     *   - no `TOC` field and no linked list among the paragraphs -> an empty map
     *   - a paragraph inside a `TOC` field      -> mapped as listed by a field; the anchor is the first `w:hyperlink`'s
     *                                              `w:anchor`, else the first internal link field's target, else the
     *                                              first `PAGEREF` target, else null
     *   - a paragraph of a linked list          -> mapped as listed by a linked list; the anchor is its link's target
     *
     * EXAMPLE
     *   paragraphs: [3] "Contents",
     *               [4] `begin` + ` TOC \o "1-3" \h ` + `separate` + hyperlink(anchor "_Toc1") "1. Definitions … 3",
     *               [5] hyperlink(anchor "_Toc2") "2. Term … 4" + `end`,
     *               [6] "1. Definitions" (carries bookmark _Toc1)
     *   -> { [4]: { "_Toc1", field }, [5]: { "_Toc2", field } }
     */
    static entriesOf(paragraphs: Element[]): Map<Element, TableOfContentsEntryTarget> {
        const entries = new Map<Element, TableOfContentsEntryTarget>();
        for (const span of TableOfContents.tableOfContentsSpansOf(paragraphs)) {
            /**
             * EXAMPLE
             *   firstParagraphIndex = 4, lastParagraphIndex = 7 -> paragraphs 4, 5, 6 and 7 are the table of contents
             */
            for (let paragraphIndex = span.firstParagraphIndex; paragraphIndex <= span.lastParagraphIndex; paragraphIndex++) {
                const paragraph = paragraphs[paragraphIndex];
                entries.set(paragraph, { anchorBookmarkName: TableOfContents.anchorBookmarkNameOf(paragraph), listedBy: "field" });
            }
        }
        for (const span of TableOfContents.linkedListSpansOf(paragraphs, entries)) {
            for (let paragraphIndex = span.firstParagraphIndex; paragraphIndex <= span.lastParagraphIndex; paragraphIndex++) {
                const paragraph = paragraphs[paragraphIndex];
                entries.set(paragraph, { anchorBookmarkName: TableOfContents.linkTallyOf(paragraph).firstAnchorBookmarkName, listedBy: "linked_list" });
            }
        }
        return entries;
    }

    /**
     * The paragraph spans of every `TOC` field, found by walking the runs of all
     * paragraphs in order with one stack of open fields, because a table of contents
     * begins in one paragraph and ends in another and no single paragraph sees both
     * markers.
     *
     * CASES, per field marker
     *   - `begin`                      -> a new open field, with an empty instruction
     *   - `instrText`                  -> appended to the innermost open field's instruction
     *   - `end` of a `TOC` field       -> a span from the paragraph of its `begin` to this one, or to
     *                                     the paragraph before when no text of this one precedes the `end`
     *   - `end` of any other field     -> the field is closed and forgotten
     *   - `separate`                   -> nothing; the result runs are the entries themselves
     *   - a `w:fldSimple` with `TOC`   -> a span of this one paragraph
     *
     * EDGE CASE
     *   A `begin` with no `end` (broken markup) never closes, so its paragraphs are
     *   not a table of contents; nothing is claimed on a guess.
     */
    private static tableOfContentsSpansOf(paragraphs: Element[]): TableOfContentsSpan[] {
        const spans: TableOfContentsSpan[] = [];
        const openFields: OpenField[] = [];
        paragraphs.forEach((paragraph, paragraphIndex) => {
            let textSeenInParagraph = false;
            TableOfContents.walkElements(paragraph, (element) => {
                const elementName = elementNameWithoutPrefix(element);
                if (TEXT_TAGS.has(elementName)) {
                    textSeenInParagraph = true;
                    return;
                }
                if (elementName === FIELD_SIMPLE_TAG && TABLE_OF_CONTENTS_INSTRUCTION.test(TableOfContents.attributeOf(element, "instr"))) {
                    spans.push({ firstParagraphIndex: paragraphIndex, lastParagraphIndex: paragraphIndex });
                    return;
                }
                if (elementName === FIELD_INSTRUCTION_TAG) {
                    /**
                     * EXAMPLE
                     *   openFields = [TOC field, PAGEREF field]
                     *   length - 1 -> 1, the PAGEREF field, whose instruction this run continues
                     */
                    const innermostOpenField = openFields[openFields.length - 1];
                    if (innermostOpenField !== undefined) innermostOpenField.instruction += element.textContent ?? "";
                    return;
                }
                if (elementName !== FIELD_CHAR_TAG) return;
                const fieldCharType = TableOfContents.attributeOf(element, "fldCharType");
                if (fieldCharType === FIELD_CHAR_TYPES.begin) openFields.push({ instruction: "", beganAtParagraphIndex: paragraphIndex });
                if (fieldCharType !== FIELD_CHAR_TYPES.end) return;
                const closedField = openFields.pop();
                if (closedField !== undefined && TABLE_OF_CONTENTS_INSTRUCTION.test(closedField.instruction)) {
                    /**
                     * WHEN the field ends before any text of this paragraph
                     *   CONSEQUENCE
                     *     this paragraph is the one after the table, carrying only the `end`
                     *     marker Word placed at its start; the last entry is the paragraph before.
                     *
                     * EXAMPLE
                     *   [7] "2. Term … 4" (last entry), [8] `end` + "Master Services Agreement"
                     *   -> firstParagraphIndex 4, lastParagraphIndex 7
                     */
                    const lastParagraphIndex = textSeenInParagraph ? paragraphIndex : paragraphIndex - 1;
                    spans.push({ firstParagraphIndex: closedField.beganAtParagraphIndex, lastParagraphIndex });
                }
            });
        });
        return spans;
    }

    /**
     * The runs of three or more consecutive paragraphs that are each wholly one
     * or more internal links, each run linking to as many distinct places as it
     * has lines, skipping paragraphs a `TOC` field already claims.
     *
     * CASES
     *   - a paragraph whose every character sits inside internal links
     *     that all point to one place                                    -> part of a run
     *   - a paragraph whose links point to two or more places            -> ends the run: a heading's edit
     *                                                                       copied onto it could land in
     *                                                                       another link's words
     *   - a paragraph with words outside its links, or with no link       -> ends the run
     *   - a run shorter than the minimum                                   -> no entries
     *   - two lines of a run linking to the same place                     -> no entries: a contents list
     *                                                                         lists each heading once, while
     *                                                                         cross-references repeat a target
     *
     * EXAMPLE
     *   paragraphs: [2] link("_h1") "1. Change kinds", [3] link("_h2") "2. Where the words sit", [4] link("_h3") "3. Fees", [5] "1. Change kinds"
     *   -> [{ firstParagraphIndex: 2, lastParagraphIndex: 4 }]
     */
    private static linkedListSpansOf(paragraphs: Element[], claimedByFields: Map<Element, TableOfContentsEntryTarget>): TableOfContentsSpan[] {
        const spans: TableOfContentsSpan[] = [];
        let linkTargetBookmarkNamesOfRun: string[] = [];
        let runStartIndex: number | null = null;
        const closeRun = (lastParagraphIndex: number): void => {
            /** WHEN no run of linked lines is open CONSEQUENCE nothing to measure */
            if (runStartIndex !== null) {
                /**
                 * EXAMPLE
                 *   runStartIndex = 2, lastParagraphIndex = 4 -> 4 - 2 + 1 = 3 paragraphs: a list
                 *   runStartIndex = 2, lastParagraphIndex = 3 -> 2 paragraphs: prose, no entries
                 */
                const lineCount = lastParagraphIndex - runStartIndex + 1;
                const distinctLinkTargetCountOfRun = new Set(linkTargetBookmarkNamesOfRun).size;
                /**
                 * WHEN the run is long enough and no two of its lines link to one place
                 *   CONSEQUENCE a contents list; a repeated target means cross-references, so no entries
                 */
                if (lineCount >= LINKED_LIST_MINIMUM_ENTRY_COUNT && distinctLinkTargetCountOfRun === lineCount) spans.push({ firstParagraphIndex: runStartIndex, lastParagraphIndex });
            }
            runStartIndex = null;
            linkTargetBookmarkNamesOfRun = [];
        };
        paragraphs.forEach((paragraph, paragraphIndex) => {
            /** WHEN a `TOC` field already lists this paragraph CONSEQUENCE no tally: the field's entry stands and the paragraph ends any linked run */
            const paragraphLinkTally = claimedByFields.has(paragraph) ? null : TableOfContents.linkTallyOf(paragraph);
            /**
             * WHEN the paragraph is wholly internal links and they all point to one place
             *   CONSEQUENCE
             *     a line of the run, listing that place.
             *
             * EXAMPLE
             *   [A "5. Payment"][X "Payment Terms Schedule"] -> two places: ends the run. Claimed, heading
             *   A's edit "Payment Terms" -> "Fee Terms" was copied onto X's words: "5. PaymentFee Terms Schedule"
             */
            if (paragraphLinkTally !== null && TableOfContents.isWhollyAnInternalLink(paragraphLinkTally) && paragraphLinkTally.firstAnchorBookmarkName !== null && paragraphLinkTally.distinctAnchorBookmarkNames.size === 1) {
                linkTargetBookmarkNamesOfRun.push(paragraphLinkTally.firstAnchorBookmarkName);
                if (runStartIndex === null) runStartIndex = paragraphIndex;
                return;
            }
            closeRun(paragraphIndex - 1);
        });
        closeRun(paragraphs.length - 1);
        return spans;
    }

    /**
     * True for a paragraph with words, all of them inside internal links.
     *
     * CASES
     *   - no linked characters                 -> false: a plain paragraph
     *   - a non-blank character outside a link -> false: prose that happens to hold a link
     */
    private static isWhollyAnInternalLink(paragraphLinkTally: ParagraphLinkTally): boolean {
        return paragraphLinkTally.linkedCharacterCount > 0 && paragraphLinkTally.unlinkedCharacterCount === 0;
    }

    /**
     * Counts a paragraph's characters inside and outside internal links, and
     * remembers the first link's target and every target its links name.
     *
     * CASES
     *   - text inside `w:hyperlink` with a `w:anchor`                       -> linked
     *   - text in the result of a complex ` HYPERLINK \l "name" ` field     -> linked
     *   - text inside a simple ` HYPERLINK \l "name" ` field                -> linked
     *   - field instructions                                                -> neither: not text Word shows
     *   - blank text outside a link                                         -> neither
     *   - any other text outside a link                                     -> unlinked
     */
    private static linkTallyOf(paragraph: Element): ParagraphLinkTally {
        const paragraphLinkTally: ParagraphLinkTally = { linkedCharacterCount: 0, unlinkedCharacterCount: 0, firstAnchorBookmarkName: null, distinctAnchorBookmarkNames: new Set() };
        const openFields: OpenFieldInParagraph[] = [];
        const noteAnchor = (anchorBookmarkName: string): void => {
            /** WHEN this is the paragraph's first link CONSEQUENCE its target is the one the line lists */
            if (paragraphLinkTally.firstAnchorBookmarkName === null) paragraphLinkTally.firstAnchorBookmarkName = anchorBookmarkName;
            paragraphLinkTally.distinctAnchorBookmarkNames.add(anchorBookmarkName);
        };
        const insideLinkField = (): boolean => openFields.some((field) => field.inResult && INTERNAL_LINK_INSTRUCTION.test(field.instruction));
        const walk = (node: Element, insideLinkElement: boolean): void => {
            for (let child = node.firstChild; child !== null; child = child.nextSibling) {
                if (child.nodeType !== ELEMENT_NODE_TYPE) continue;
                const element = child as Element;
                const elementName = elementNameWithoutPrefix(element);
                if (elementName === HYPERLINK_TAG) {
                    const anchor = TableOfContents.attributeOf(element, "anchor");
                    if (anchor.length > 0) noteAnchor(anchor);
                    walk(element, insideLinkElement || anchor.length > 0);
                    continue;
                }
                if (elementName === FIELD_SIMPLE_TAG) {
                    const link = INTERNAL_LINK_INSTRUCTION.exec(TableOfContents.attributeOf(element, "instr"));
                    if (link !== null) noteAnchor(link[1]);
                    walk(element, insideLinkElement || link !== null);
                    continue;
                }
                if (elementName === FIELD_CHAR_TAG) {
                    const fieldCharType = TableOfContents.attributeOf(element, "fldCharType");
                    if (fieldCharType === FIELD_CHAR_TYPES.begin) openFields.push({ instruction: "", inResult: false });
                    const innermost = openFields[openFields.length - 1];
                    if (fieldCharType === FIELD_CHAR_TYPES.separate && innermost !== undefined) {
                        innermost.inResult = true;
                        const link = INTERNAL_LINK_INSTRUCTION.exec(innermost.instruction);
                        if (link !== null) noteAnchor(link[1]);
                    }
                    if (fieldCharType === FIELD_CHAR_TYPES.end) openFields.pop();
                    continue;
                }
                if (elementName === FIELD_INSTRUCTION_TAG) {
                    const innermost = openFields[openFields.length - 1];
                    if (innermost !== undefined) innermost.instruction += element.textContent ?? "";
                    continue;
                }
                if (TEXT_TAGS.has(elementName)) {
                    const text = element.textContent ?? "";
                    if (insideLinkElement || insideLinkField()) paragraphLinkTally.linkedCharacterCount += text.length;
                    else paragraphLinkTally.unlinkedCharacterCount += text.trim().length;
                    continue;
                }
                walk(element, insideLinkElement);
            }
        };
        walk(paragraph, false);
        return paragraphLinkTally;
    }

    /**
     * The heading bookmark an entry paragraph points at, taken from the first
     * element in document order that names one.
     *
     * FALLBACK
     *   a `w:hyperlink` with a `w:anchor`   -> that anchor (the `\h` switch's link)
     *   a `HYPERLINK \l` instruction        -> its bookmark (an entry built as a link field)
     *   a `PAGEREF` instruction             -> its bookmark (every entry draws a page number)
     *   none of them                        -> null: an entry that points nowhere
     */
    private static anchorBookmarkNameOf(paragraph: Element): string | null {
        let anchorBookmarkName: string | null = null;
        TableOfContents.walkElements(paragraph, (element) => {
            if (anchorBookmarkName !== null) return;
            const elementName = elementNameWithoutPrefix(element);
            if (elementName === HYPERLINK_TAG) {
                const anchor = TableOfContents.attributeOf(element, "anchor");
                if (anchor.length > 0) anchorBookmarkName = anchor;
                return;
            }
            if (elementName !== FIELD_INSTRUCTION_TAG) return;
            const instruction = element.textContent ?? "";
            const internalLink = INTERNAL_LINK_INSTRUCTION.exec(instruction);
            if (internalLink !== null) {
                anchorBookmarkName = internalLink[1];
                return;
            }
            const pageReference = PAGE_REFERENCE_INSTRUCTION.exec(instruction);
            if (pageReference !== null) anchorBookmarkName = pageReference[1];
        });
        return anchorBookmarkName;
    }

    /** Visits `root`'s descendant elements in document order, each before its children, the order Word reads runs and markers in. */
    private static walkElements(root: Element, visit: (element: Element) => void): void {
        for (let child = root.firstChild; child !== null; child = child.nextSibling) {
            if (child.nodeType !== ELEMENT_NODE_TYPE) continue;
            const element = child as Element;
            visit(element);
            TableOfContents.walkElements(element, visit);
        }
    }

    /**
     * A WordprocessingML attribute by local name.
     *
     * FALLBACK
     *   namespace-aware read   -> use it
     *   prefixed `w:` read     -> otherwise use it (a parser that kept prefixes as names)
     *   missing both           -> ""
     */
    private static attributeOf(element: Element, localName: string): string {
        return element.getAttributeNS(WORDPROCESSINGML_NS, localName) || element.getAttribute(`w:${localName}`) || "";
    }
}
